/**
 * VOICE-001 — the microphone's lifecycle, with no React in it.
 *
 * It lives apart from the hook so that it can be driven in node with fakes: a
 * static render never runs effects, and the failures that matter here — a note
 * lost at the time limit, a microphone left on after cancelling, a stop on a
 * recorder that has already stopped — are all about the order things happen in.
 *
 * Opus in WebM where the browser has it (Chrome, Android WebView), MP4/AAC
 * otherwise (Safari); the speech service decodes either.
 */
export const VOICE_MIME = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

export type VoiceRecorderState = 'idle' | 'armed' | 'recording' | 'paused' | 'denied';

/**
 * How often the clock is read while recording. The count shown is whole
 * seconds, but the limit is checked against the clock itself, so a take never
 * runs more than this far past it — the server allows a few seconds of slack
 * before it calls a note too long, and this stays well inside them.
 */
const CLOCK_MS = 250;

export interface VoiceRecorderDeps {
  getUserMedia(): Promise<MediaStream>;
  MediaRecorderCtor: new (stream: MediaStream, options?: MediaRecorderOptions) => MediaRecorder;
  isTypeSupported(mime: string): boolean;
  maxSeconds: number;
  onTick(seconds: number): void;
  onState(state: VoiceRecorderState): void;
  /** The note that reached the limit and stopped itself; nobody called stop(). */
  onAutoStop(blob: Blob | null): void;
  setInterval?: (fn: () => void, ms: number) => unknown;
  clearInterval?: (handle: unknown) => void;
  /** Milliseconds from any fixed point; injected so tests can move time by hand. */
  now?: () => number;
}

export function createVoiceRecorder(deps: VoiceRecorderDeps) {
  const setTimer = deps.setInterval ?? ((fn, ms) => globalThis.setInterval(fn, ms));
  const clearTimer = deps.clearInterval ?? ((handle) => globalThis.clearInterval(handle as never));
  const now = deps.now ?? (() => Date.now());

  let recorder: MediaRecorder | null = null;
  let armed = false;
  let chunks: Blob[] = [];
  let timer: unknown = null;
  let done: ((blob: Blob | null) => void) | null = null;
  let autoStopping = false;
  // Bumped by discard and dispose. A start() that is still waiting for the
  // permission prompt compares it after the await: if it moved, the person has
  // gone, and the stream that arrives must be let go rather than recorded.
  let generation = 0;
  // The generation a start() is waiting in, or null. Keyed to the generation
  // rather than a plain flag because the screen now starts recording the moment
  // it opens, and React's StrictMode opens it twice: mount, clean up (discard),
  // mount again. With a flag the second start saw the first one still waiting
  // and returned, the first then found itself discarded and let its stream go —
  // and nothing recorded at all. A start in an abandoned generation no longer
  // blocks one in the current generation.
  let pending: number | null = null;

  /*
   * Recorded time, not wall time. A pause banks what the segment so far was
   * worth and stops the clock; resume opens a new segment. The limit is on what
   * goes into the file, so time spent paused must not count against it — and
   * it must not be lost either, which is why this is milliseconds and not the
   * whole-second count: dropping the part-second at every pause would let a
   * take paused often enough run past the limit with the count still under it.
   */
  let banked = 0;
  let segmentStart = 0;
  let shown = 0;
  const recordedMs = () => banked + (recorder?.state === 'recording' ? now() - segmentStart : 0);

  const stopClock = () => {
    if (timer !== null) clearTimer(timer);
    timer = null;
  };

  const release = () => {
    stopClock();
    recorder?.stream.getTracks().forEach((track) => track.stop());
    recorder = null;
  };

  const stopRecorder = (rec: MediaRecorder) => {
    // Stopping a recorder that is already inactive throws InvalidStateError.
    if (rec.state !== 'inactive') rec.stop();
  };

  const runClock = (rec: MediaRecorder) => {
    segmentStart = now();
    timer = setTimer(() => {
      if (armed) {
        if (now() - segmentStart >= 30000) {
          rec.onstop = null; stopRecorder(rec); release(); chunks = []; armed = false; deps.onState('idle');
        }
        return;
      }
      const ms = recordedMs();
      const seconds = Math.min(deps.maxSeconds, Math.floor(ms / 1000));
      if (seconds !== shown) {
        shown = seconds;
        deps.onTick(seconds);
      }
      if (ms >= deps.maxSeconds * 1000) {
        // Ticking ends here, so the count never reads past the limit.
        stopClock();
        autoStopping = true;
        stopRecorder(rec);
      }
    }, CLOCK_MS);
  };

  const stop = () =>
    new Promise<Blob | null>((resolve) => {
      const active = recorder;
      if (!active || active.state === 'inactive') return resolve(null);
      done = resolve;
      active.stop();
    });

  const start = async (arm = false) => {
    if (recorder || pending === generation) return;
    const mine = generation;
    pending = mine;
    let stream: MediaStream;
    try {
      stream = await deps.getUserMedia();
    } catch {
      if (pending === mine) pending = null;
      if (mine === generation) deps.onState('denied');
      return;
    }
    if (pending === mine) pending = null;
    if (mine !== generation || recorder) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    const mimeType = VOICE_MIME.find((m) => deps.isTypeSupported(m));
    const rec = new deps.MediaRecorderCtor(stream, mimeType ? { mimeType } : undefined);
    chunks = [];
    armed = arm;
    autoStopping = false;
    banked = 0;
    shown = 0;
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);

    };
    rec.onstop = () => {
      const blob = chunks.length > 0 ? new Blob(chunks, { type: rec.mimeType }) : null;
      release();
      deps.onState('idle');
      if (done) {
        const resolve = done;
        done = null;
        resolve(blob);
      } else if (autoStopping) {
        deps.onAutoStop(blob);
      }
      autoStopping = false;
    };
    recorder = rec;
    rec.start(1000);
    deps.onTick(0);
    deps.onState(armed ? 'armed' : 'recording');
    runClock(rec);
  };

  /** Holds the take. The microphone stays open, so resume is instant. */
  const pause = () => {
    const rec = recorder;
    if (!rec || rec.state !== 'recording') return;
    banked += now() - segmentStart;
    stopClock();
    rec.pause();
    deps.onState('paused');
  };

  const resume = () => {
    const rec = recorder;
    if (!rec || rec.state !== 'paused') return;
    rec.resume();
    deps.onState(armed ? 'armed' : 'recording');
    runClock(rec);
  };

  const discard = () => {
    generation += 1;
    done = null;
    autoStopping = false;
    const rec = recorder;
    if (rec) {
      rec.onstop = null;
      stopRecorder(rec);
    }
    release();
    deps.onState('idle');
  };

  // Not terminal: React's StrictMode mounts, cleans up and mounts again on the
  // same instance, so a dispose that shut the recorder for good left the button
  // dead in development. It lets go of everything, and a pending permission
  // is still released through the generation, but a later start() works.
  const dispose = () => {
    discard();
  };

  /**
   * The live microphone, for a level meter to listen to while recording. The
   * same stream the recorder writes from: asking getUserMedia a second time
   * would mean a second permission path on some browsers and a second capture
   * on all of them, for the sake of a picture.
   */
  const stream = (): MediaStream | null => recorder?.stream ?? null;

  const activate = () => {
    if (!armed || !recorder) return;
    banked += now() - segmentStart;
    armed = false;
    stopClock();
    deps.onState('recording');
    runClock(recorder);
  };
  return { start, activate, stop, pause, resume, discard, dispose, stream };
}
